import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Tag, Grid, LogOut, X, ChevronLeft, MessageSquare, Images, Users, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminSidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/requests', label: 'Requests', icon: MessageSquare },
    { path: '/admin/brands', label: 'Brands', icon: Tag },
    { path: '/admin/categories', label: 'Categories', icon: Grid },
    { path: '/admin/banners', label: 'Banners', icon: Images },
    { path: '/admin/promocodes', label: 'Promo Codes', icon: Ticket },
    { path: '/admin/users', label: 'Users', icon: Users }
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_username');
    toast.success('Logged out successfully');
    navigate('/admin/login');
    onClose();
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
          w-64 flex flex-col
        `}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className={`${isCollapsed ? 'lg:hidden' : ''}`}>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-[#0047AB]" />
              <div>
                <span className="text-2xl font-outfit font-bold tracking-tighter text-slate-900">
                  DXB<span className="text-[#0047AB]">2</span>BOM
                </span>
                <p className="text-xs text-slate-500 mt-1">Admin Panel</p>
              </div>
            </div>
          </div>
          <div className={`${isCollapsed ? 'lg:mx-auto' : 'lg:hidden'}`}>
            <Package className={`w-8 h-8 text-[#0047AB] ${isCollapsed ? '' : 'lg:hidden'}`} />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Collapse Toggle - Desktop Only */}
        <div className="hidden lg:block p-3 border-b border-slate-200">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center"
            onClick={onToggleCollapse}
          >
            <ChevronLeft className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6">
          <div className="space-y-1 px-3">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg font-dm-sans
                    transition-colors duration-200
                    ${active 
                      ? 'bg-[#0047AB] text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                    }
                    ${isCollapsed ? 'lg:justify-center' : ''}
                  `}
                  title={isCollapsed ? item.label : ''}
                  data-testid={`admin-sidebar-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`font-medium ${isCollapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-slate-200">
          <Button
            variant="ghost"
            className={`w-full text-red-600 hover:text-red-700 hover:bg-red-50 ${isCollapsed ? 'lg:justify-center lg:px-2' : 'justify-start'}`}
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : ''}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className={`font-medium ml-3 ${isCollapsed ? 'lg:hidden' : ''}`}>Logout</span>
          </Button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;